import React, { useEffect, useRef, useState } from 'react';
import { DonorGiftOffer, DonorGiftOrder, DonorGiftsView, fetchDonorGiftOrder, fetchDonorGifts, recordDonorGiftOfferViewed, startDonorGiftCheckout } from '../../api/donorGiftsApi';
import StoryArtwork from '../../components/StoryArtwork/StoryArtwork';
import { Chip, FormField, PillButton, PolicyDisclosure, Shelf } from '../../components/ui';

const finalStatuses = new Set(['fulfilled', 'refunded', 'disputed', 'payment_failed', 'failed', 'canceled']);
function checkoutKey(product: string): string {
  const key = `luma-gift-checkout:${product}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const value = crypto.randomUUID();
    sessionStorage.setItem(key, value);
    return value;
  } catch { return crypto.randomUUID(); }
}
export function giftPrice(amount: number, currency: string): string {
  const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
  const digits = formatter.resolvedOptions().maximumFractionDigits;
  return formatter.format(amount / (10 ** digits));
}

/** "Maya", "Maya and Leo", or "Maya, Leo and Sam". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function scopeLabel(offer: DonorGiftOffer): string {
  return offer.content_scope?.label || 'Included content is awaiting review.';
}

/** Server-provided scope sentence plus the protected partial-content notice. */
function scopeSentence(offer: DonorGiftOffer): string {
  return `${scopeLabel(offer)}${offer.content_scope?.kind !== 'complete_story' ? ' This chapter or segment does not include the entire chapter book.' : ''}`;
}

/** The artwork endpoint is public; the id is opaque catalog data. */
function artworkUrl(storyId: string): string {
  return `/api/stories/${encodeURIComponent(storyId)}/artwork`;
}

const giftPolicy = (
  <>
    <p>Your recordings are saved. You can also give one initial personalized bedtime from an eligible title, parent approval, and ongoing in-app replay of that approved gift without a subscription.</p>
    <p>The household chooses the voice and listener and approves playback. Buying a gift does not start voice generation or give you access to their household. No account required.</p>
    <p>After activation, Luma-issued refunds are for Luma technical failures following support review. Payment-provider refunds and disputes are handled separately.</p>
  </>
);

interface DonorGiftFlowProps {
  /** Bounded listener display names from the invitation presentation. */
  listenerNames?: string[];
  headingLevel?: 1 | 2;
}

/** Payment returns are hints only: all commercial state comes from the session-scoped API. */
export default function DonorGiftFlow({ listenerNames = [], headingLevel = 2 }: DonorGiftFlowProps) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const [query, setQuery] = useState('');
  const [connected, setConnected] = useState(navigator.onLine);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => { const update = () => setConnected(navigator.onLine); window.addEventListener('online', update); window.addEventListener('offline', update); return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); }; }, []);
  const [view, setView] = useState<DonorGiftsView | null>(null);
  const [order, setOrder] = useState<DonorGiftOrder | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const inFlight = useRef(false);
  const viewed = useRef(false);
  const keys = useRef<Record<string, string>>({});
  const returnParams = useRef(new URLSearchParams(window.location.search));
  const canceled = returnParams.current.get('checkout') === 'cancel';
  const returnedOrder = returnParams.current.get('gift_order');

  useEffect(() => {
    let active = true;
    void fetchDonorGifts().then(async (next) => {
      const current = returnedOrder
        ? await fetchDonorGiftOrder(returnedOrder)
        : next.orders[0] ?? null;
      if (active) { setView(next); setOrder(current); }
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [returnedOrder, refresh]);

  useEffect(() => {
    if (!order || finalStatuses.has(order.status) || !connected) return;
    let active = true;
    const timer = window.setInterval(() => {
      void fetchDonorGiftOrder(order.id).then((next) => {
        if (active) { setOrder(next); setError(false); }
      }).catch(() => { if (active) setError(true); });
    }, 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [order, connected]);

  useEffect(() => {
    if (!skipped && !order && view?.offers.length && !viewed.current) {
      viewed.current = true;
      void recordDonorGiftOfferViewed();
    }
  }, [order, skipped, view]);

  const start = async (product: string) => {
    if (inFlight.current || !connected) return;
    inFlight.current = true; setBusy(true); setError(false);
    try {
      keys.current[product] ??= checkoutKey(product);
      const next = await startDonorGiftCheckout(product, keys.current[product]);
      if (next.checkout_url) {
        const url = new URL(next.checkout_url);
        if (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.hostname === 'localhost')) throw new Error('Invalid checkout URL');
        window.location.assign(url.href);
      } else {
        setOrder(await fetchDonorGiftOrder(next.order_id));
      }
    } catch { setError(true); }
    finally { inFlight.current = false; setBusy(false); }
  };

  if (skipped) return <><Heading>Your recordings are saved</Heading><p className="donor-note">You’re all done. Your recordings are saved, and you can close this page.</p></>;
  if (!view && !error) return <><Heading>Checking gift offers</Heading><p role="status" aria-busy="true">Your recordings are saved.</p><PillButton onClick={() => setSkipped(true)}>Finish without a gift</PillButton></>;

  const canResume = order && (canceled || ['payment_failed', 'failed', 'canceled'].includes(order.status)
    || (!returnedOrder && ['created', 'checkout_pending'].includes(order.status)));
  const paid = order && ['paid', 'payment_confirmed', 'fulfillment_pending'].includes(order.status);
  const offers = view?.offers ?? [];
  const matchingOffers = offers.filter(offer => `${offer.title} ${offer.description}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const offlineNotice = !connected && <p className="donor-note" role="status">Connect to the internet to check gift status or continue checkout. You can finish without a gift.</p>;
  // The first offer starts selected so the one primary action is always
  // present; choosing another tile moves the selection and the summary.
  const selected = offers.find((offer) => offer.product_key === selectedKey) ?? offers[0] ?? null;
  const orderedOffer = order ? offers.find((offer) => offer.product_key === order.product_key) : null;
  const heading = order?.status === 'fulfilled'
    ? 'Your gift is ready for the family'
    : order?.status === 'refunded' ? 'Gift refunded'
    : order?.status === 'disputed' ? 'Payment under review'
    : paid ? 'Your gift is on its way'
    : order ? canResume ? 'Finish your gift' : 'Checking your gift payment'
    : listenerNames.length === 0
      ? 'Give a bedtime story'
      : `Gift ${joinNames(listenerNames)} a story tonight`;

  if (order) {
    return (
      <section className="donor-gift" aria-labelledby="donor-gift-heading">
        <Heading id="donor-gift-heading">{heading}</Heading>
        {offlineNotice}
        {error && <p className="donor-error" role="alert">We couldn’t confirm the latest gift status. Your recordings are safe. Please refresh this page before starting another purchase.</p>}
        <div className="donor-gift__status" aria-live="polite">
          <h3>{order.title}</h3>
          {order.status === 'fulfilled' ? <p>The household can now choose a permitted voice and listener, prepare the bedtime, and approve it for playback. No account is needed from you.</p>
            : order.status === 'refunded' ? <p>This gift has been refunded. The household’s access follows the refund policy.</p>
            : order.status === 'disputed' ? <p>This payment is under review. Gift preparation is paused while it is resolved.</p>
            : paid ? <p>Payment confirmed. We’re delivering your gift to the household. You do not need to pay again.</p>
            : canResume ? <p>Checkout wasn’t completed. Your recordings are saved. You may continue checkout or finish without a gift.</p>
            : <p>Confirming payment… We’ll show the gift here once the payment provider confirms it. Please don’t start another purchase.</p>}
        </div>
        <PolicyDisclosure title="Gift details" className="donor-gift__details">
          {orderedOffer && (
            <p>
              {scopeSentence(orderedOffer)} {giftPrice(orderedOffer.amount_minor, orderedOffer.currency)} {orderedOffer.currency.toUpperCase()} · one-time gift.
            </p>
          )}
          {giftPolicy}
        </PolicyDisclosure>
        <div className="donor-actions">
          {canResume && !paid && !['fulfilled', 'refunded', 'disputed'].includes(order.status) && view?.checkout_enabled && (
            <PillButton variant="primary" disabled={!connected} busy={busy} busyLabel="Opening checkout…" onClick={() => void start(order.product_key)}>Continue checkout</PillButton>
          )}
          <PillButton variant="secondary" onClick={() => setSkipped(true)}>Done for now</PillButton>
        </div>
      </section>
    );
  }

  return (
    <div className="donor-gift">
      {error && <p className="donor-error" role="alert">We couldn’t confirm the latest gift status. Your recordings are safe. Please refresh this page before starting another purchase.</p>}
      <Heading id="donor-gift-heading">{heading}</Heading>
      <p>A story in your voice is a lovely way to say good night. Adding a gift is optional.</p>
      {offlineNotice}
      {offers.length > 0 && <>
        <FormField id="donor-gift-search" label="Search gift offers"><input type="search" value={query} onChange={event => setQuery(event.target.value)} /></FormField>
        <fieldset className="donor-gift__choices"><legend className="luma-sr-only">Choose a gift offer</legend>
          <Shelf id="donor-gift-offers" title="Choose a story" items={matchingOffers} getKey={offer => offer.product_key} aspectRatio="1/1" className="donor-gift__shelf" renderItem={offer => <div className="donor-gift__choice">
            <StoryArtwork src={artworkUrl(offer.story_id)} alt="" imageClassName="donor-gift__artwork" fallbackClassName="donor-gift__artwork" />
            <Chip radioName="donor-gift-offer" radioValue={offer.product_key} label={offer.title} selected={selected?.product_key === offer.product_key} disabled={busy} onSelect={() => setSelectedKey(offer.product_key)} controls="donor-gift-selected" />
          </div>} />
        </fieldset>
        {!matchingOffers.length && <p role="status">No matching gift offers. Try another search or clear your search.</p>}
      </>}
      {!offers.length && <p role="status">Gift offers are unavailable right now. Your recordings are saved.</p>}
      {error && <PillButton disabled={busy || !connected} onClick={() => { setError(false); setRefresh(value => value + 1); }}>Check gift offers again</PillButton>}
      {selected ? (
        <section id="donor-gift-selected" aria-labelledby="donor-gift-selected-title" className="donor-gift__summary">
          <h3 id="donor-gift-selected-title">{selected.title}</h3>
          <p>{selected.description}</p>
          <p className="donor-note">{scopeSentence(selected)}</p>
          <p className="donor-gift__price">
            <span className="donor-gift__amount">{giftPrice(selected.amount_minor, selected.currency)}</span>
            <span className="donor-note">{selected.currency.toUpperCase()} · one-time gift</span>
          </p>
        </section>
      ) : null}
      <PolicyDisclosure title="Gift details" className="donor-gift__details">
        {giftPolicy}
      </PolicyDisclosure>
      {view && !view.checkout_enabled && <p className="donor-note" role="status">Gift checkout is temporarily unavailable. You can finish without a gift.</p>}
      <div className="donor-actions">
        {selected && (
          <PillButton
            variant="primary"
            busy={busy}
            busyLabel="Opening checkout…"
            disabled={!view?.checkout_enabled || !connected}
            onClick={() => void start(selected.product_key)}
          >
            Continue to checkout
          </PillButton>
        )}
        <PillButton variant="secondary" onClick={() => setSkipped(true)}>Finish without a gift</PillButton>
      </div>
    </div>
  );
}
